/**
 * Composes the validation result panels for the infrastructure import modal.
 *
 * Extracted from ImportModal.tsx (Sub-PR 6.4).
 */

import { useTranslation } from 'react-i18next'
import { Loader2, Upload } from 'lucide-react'
import type { DuplicateAction, ImportValidationResult } from '../../../types/infrastructure'
import {
  DuplicatesInDbSection,
  DuplicatesInFileSection,
  ErrorsSection,
  IpConflictsSection,
  PreviewSection,
  ResultSummary,
  WarningsSection,
} from './ValidationSections'

interface ValidationResultsDisplayProps {
  result: ImportValidationResult
  onExecute: (action: DuplicateAction) => void | Promise<void>
  onCancel: () => void
  loading: boolean
}

interface ActionsBarProps {
  result: ImportValidationResult
  canImport: boolean
  loading: boolean
  onExecute: (action: DuplicateAction) => void | Promise<void>
  onCancel: () => void
}

function ActionsBar({
  result,
  canImport,
  loading,
  onExecute,
  onCancel,
}: ActionsBarProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const importableCount = result.validRecords - result.duplicates.hostnameInDatabase.length

  return (
    <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
      <button
        onClick={onCancel}
        className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      {canImport && (
        <button
          onClick={() => void onExecute('skip_all')}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('infrastructure.import.importing')}
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              {t('infrastructure.import.importRecords', { count: importableCount })}
            </>
          )}
        </button>
      )}
    </div>
  )
}

export function ValidationResultsDisplay({
  result,
  onExecute,
  onCancel,
  loading,
}: ValidationResultsDisplayProps): React.ReactElement {
  const hasDuplicatesInFile = result.duplicates.hostnameInFile.length > 0
  const hasDuplicatesInDb = result.duplicates.hostnameInDatabase.length > 0
  const hasIpConflicts = result.duplicates.ipAddressConflicts.length > 0
  const hasErrors = result.errors.length > 0
  const hasWarnings = result.warnings.length > 0
  const canImport = result.valid && !hasDuplicatesInFile

  return (
    <div className="space-y-6">
      <ResultSummary result={result} />
      {hasErrors && <ErrorsSection result={result} />}
      {hasDuplicatesInFile && <DuplicatesInFileSection result={result} />}
      {hasDuplicatesInDb && <DuplicatesInDbSection result={result} />}
      {hasIpConflicts && <IpConflictsSection result={result} />}
      {hasWarnings && <WarningsSection result={result} />}
      {result.preview.length > 0 && <PreviewSection result={result} />}
      <ActionsBar
        result={result}
        canImport={canImport}
        loading={loading}
        onExecute={onExecute}
        onCancel={onCancel}
      />
    </div>
  )
}
